import { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { Users, Upload, RefreshCw, Trash2, List, GitFork, UserPlus, Download, CircleAlert as AlertCircle, Building2, Crown, Loader as Loader2, Check, X, MoveHorizontal as MoreHorizontal } from 'lucide-react';
import { useApi } from '../../../lib/useApi';
import type { Account, Contact, UseCaseItem, ContactUseCaseScore, OutboundPriority, AccountDepartment } from '../../../types';
import {
  autoAssignHierarchy,
  buildInferredExecutiveContacts,
  type MissingPersona,
} from '../../../lib/orgChartUtils';
import LoadingSpinner from '../../common/LoadingSpinner';
import ContactCsvUpload from './ContactCsvUpload';
import ContactSearchBar from './ContactSearchBar';
import OrgChartTree from './OrgChartTree';
import OrgChartList from './OrgChartList';
import OrgChartDepartmentView from './OrgChartDepartmentView';
import ContactDetailPanel from './ContactDetailPanel';
import ManagerAssignModal from './ManagerAssignModal';
import AddContactModal from './AddContactModal';

interface Props {
  account: Account;
  contacts: Contact[];
  departments: AccountDepartment[];
  selectedUseCase: UseCaseItem | null;
  ucScores: ContactUseCaseScore[];
  scoringInProgress: boolean;
  missingPersonas: MissingPersona[];
  researchedPersonas: { personaImpactMap: { persona: string; strategic_accountability: string; executive_risk: string; vendor_capability: string }[]; valueStatements: { persona: string; statement_text: string; proof_point: string }[] } | null;
  selectedContactId: string | null;
  onContactsChanged: (contacts: Contact[]) => void;
  onGoToMessaging: (contactId: string) => void;
  onPriorityOverride: (scoreId: string, priority: OutboundPriority | null, justification: string) => void;
  onContactSelect: (contactId: string | null) => void;
}

type ViewMode = 'tree' | 'list' | 'departments';

export default function OrgChartSubTab({
  account,
  contacts,
  departments,
  selectedUseCase,
  ucScores,
  scoringInProgress,
  missingPersonas,
  researchedPersonas,
  selectedContactId,
  onContactsChanged,
  onGoToMessaging,
  onPriorityOverride,
  onContactSelect,
}: Props) {
  const { apiGet, apiPost, apiPut, apiDelete } = useApi();
  const selectedContact = useMemo(() => {
    return selectedContactId ? contacts.find(c => c.id === selectedContactId) || null : null;
  }, [selectedContactId, contacts]);
  const [showUpload, setShowUpload] = useState(false);
  const [showAddContact, setShowAddContact] = useState(false);
  const [rebuilding, setRebuilding] = useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>('tree');
  const [managerAssignContact, setManagerAssignContact] = useState<Contact | null>(null);
  const [focusContactId, setFocusContactId] = useState<string | null>(null);
  const [execSeedDismissed, setExecSeedDismissed] = useState(false);
  const [seedingExecs, setSeedingExecs] = useState(false);
  const [showActions, setShowActions] = useState(false);
  const actionsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!showActions) return;
    const handler = (e: MouseEvent) => {
      if (actionsRef.current && !actionsRef.current.contains(e.target as Node)) {
        setShowActions(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showActions]);

  const handleSearchSelect = useCallback((contact: Contact) => {
    onContactSelect(contact.id);
    setFocusContactId(contact.id);
  }, [onContactSelect]);

  const scoreMap = new Map<string, ContactUseCaseScore>();
  for (const s of ucScores) {
    scoreMap.set(s.contact_id, s);
  }

  const inferredExecs = useMemo(() => {
    if (execSeedDismissed) return [];
    return buildInferredExecutiveContacts(account, contacts);
  }, [account, contacts, execSeedDismissed]);

  const handleSeedExecutives = useCallback(async () => {
    if (inferredExecs.length === 0) return;
    setSeedingExecs(true);

    const inserted: Contact[] = [];
    for (const exec of inferredExecs) {
      try {
        // account_id is owned by the endpoint (path), not the body
        const { contact } = await apiPost<{ contact: Contact }>(
          `/v1/account/${account.id}/contacts`,
          {
            first_name: exec.first_name,
            last_name: exec.last_name,
            role: exec.role,
            organisation: exec.organisation,
            email: '',
            phone: '',
            department: exec.department,
            seniority: exec.seniority,
            linkedin_url: '',
            location: '',
            buying_committee_role: exec.buying_committee_role,
            org_level: exec.org_level,
            org_placement_confidence: exec.org_placement_confidence,
            notes: '',
            source: 'inferred_executive',
            is_inferred: true,
          }
        );
        if (contact) inserted.push(contact);
      } catch {
        // skip a failed insert, matching the legacy `if (data) inserted.push(data)`
        // which silently dropped inserts that returned no row
      }
    }

    if (inserted.length > 0) {
      const all = [...contacts, ...inserted];
      const withHierarchy = autoAssignHierarchy(all);

      try {
        await apiPut(`/v1/account/${account.id}/contacts/hierarchy`, {
          updates: withHierarchy.map(c => ({
            id: c.id,
            reports_to_id: c.reports_to_id,
            org_placement_confidence: c.org_placement_confidence,
          })),
        });
      } catch {
        // silent — keep prior no-op-on-error behaviour (the legacy supabase batch
        // update swallowed RLS rejections and applied the local update either way)
      }

      onContactsChanged(withHierarchy);
    }

    setExecSeedDismissed(true);
    setSeedingExecs(false);
  }, [inferredExecs, contacts, account.id, apiPost, apiPut, onContactsChanged]);

  const handleUpload = useCallback(async (newContacts: Contact[]) => {
    const all = [...contacts, ...newContacts];
    const withHierarchy = autoAssignHierarchy(all);

    try {
      await apiPut(`/v1/account/${account.id}/contacts/hierarchy`, {
        updates: withHierarchy.map(c => ({
          id: c.id,
          reports_to_id: c.reports_to_id,
          org_placement_confidence: c.org_placement_confidence,
        })),
      });
    } catch {
      // silent — keep prior no-op-on-error behaviour (the legacy supabase batch
      // update swallowed RLS rejections and applied the local update either way)
    }

    onContactsChanged(withHierarchy);
    setShowUpload(false);
  }, [contacts, account.id, apiPut, onContactsChanged]);

  const handleRebuildHierarchy = useCallback(async () => {
    setRebuilding(true);
    setShowActions(false);
    const withHierarchy = autoAssignHierarchy(contacts);

    try {
      await apiPut(`/v1/account/${account.id}/contacts/hierarchy`, {
        updates: withHierarchy.map(c => ({
          id: c.id,
          reports_to_id: c.reports_to_id,
          org_placement_confidence: c.org_placement_confidence,
          department: c.department,
          seniority: c.seniority,
          org_level: c.org_level,
          buying_committee_role: c.buying_committee_role,
        })),
      });
    } catch {
      // silent — keep prior no-op-on-error behaviour (the legacy supabase batch
      // update swallowed RLS rejections)
    }

    try {
      const { contacts: refreshed } = await apiGet<{ contacts: Contact[] }>(
        `/v1/account/${account.id}/contacts`
      );
      if (refreshed) onContactsChanged(refreshed);
    } catch {
      // silent — keep the prior contacts on a failed re-read (legacy `if (data)`
      // only updated when the select returned rows)
    }
    setRebuilding(false);
  }, [contacts, account.id, apiGet, apiPut, onContactsChanged]);

  const handleContactUpdate = useCallback((updated: Contact) => {
    const newContacts = contacts.map(c => c.id === updated.id ? updated : c);
    onContactsChanged(newContacts);
    onContactSelect(updated.id);
  }, [contacts, onContactsChanged, onContactSelect]);

  const handleDeleteAll = useCallback(async () => {
    setShowActions(false);
    if (!confirm('Delete all contacts for this account? This cannot be undone.')) return;
    await apiDelete(`/v1/account/${account.id}/contacts`);
    onContactsChanged([]);
    onContactSelect(null);
  }, [account.id, apiDelete, onContactsChanged, onContactSelect]);

  const handleContactAdded = useCallback((newContact: Contact) => {
    const updated = [...contacts, newContact];
    onContactsChanged(updated);
    setShowAddContact(false);
  }, [contacts, onContactsChanged]);

  const handleExportCsv = useCallback(() => {
    if (contacts.length === 0) return;
    setShowActions(false);
    const headers = ['First Name', 'Last Name', 'Title', 'Email', 'Phone', 'LinkedIn URL', 'Department', 'Seniority', 'Location', 'Company', 'Buying Committee Role', 'Notes'];
    const rows = contacts.map(c => [
      c.first_name,
      c.last_name,
      c.role,
      c.email,
      c.phone,
      c.linkedin_url,
      c.department,
      c.seniority,
      c.location,
      c.organisation,
      c.buying_committee_role,
      c.notes,
    ].map(v => {
      const str = (v || '').toString();
      return str.includes(',') || str.includes('"') || str.includes('\n')
        ? `"${str.replace(/"/g, '""')}"`
        : str;
    }));
    const csv = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${account.company_name.replace(/[^a-zA-Z0-9]/g, '_')}_contacts.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [contacts, account.company_name]);

  const handleAssignManager = useCallback(async (contactId: string, managerId: string | null) => {
    try {
      // updated_at is bumped server-side by the endpoint
      await apiPut(`/v1/contacts/${contactId}`, { reports_to_id: managerId });
    } catch {
      // silent — keep prior no-op-on-error behaviour (legacy supabase update
      // swallowed RLS rejections; the optimistic local update is applied either way)
    }

    const newContacts = contacts.map(c =>
      c.id === contactId ? { ...c, reports_to_id: managerId } : c
    );
    onContactsChanged(newContacts);

    if (selectedContact?.id === contactId) {
      onContactSelect(contactId);
    }

    setManagerAssignContact(null);
  }, [apiPut, contacts, onContactsChanged, selectedContact, onContactSelect]);

  if (contacts.length === 0 && !showUpload) {
    return (
      <div className="space-y-6">
        {inferredExecs.length > 0 && !execSeedDismissed && (
          <ExecutiveSeedBanner
            count={inferredExecs.length}
            executives={inferredExecs}
            onSeed={handleSeedExecutives}
            onDismiss={() => setExecSeedDismissed(true)}
            seeding={seedingExecs}
          />
        )}
        <div className="bg-app-canvas border border-app-line rounded-lg p-10 text-center">
          <Users className="w-12 h-12 text-app-line-strong mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-app-ink mb-2">No Contacts Yet</h3>
          <p className="text-sm text-app-muted max-w-md mx-auto mb-6">
            Upload a CSV of contacts or add them manually to start building your org chart and outreach plan.
          </p>
          <div className="flex items-center justify-center gap-3">
            <button onClick={() => setShowUpload(true)} className="btn-primary">
              <Upload className="w-4 h-4 mr-2" /> Upload CSV
            </button>
            <button onClick={() => setShowAddContact(true)} className="btn-secondary">
              <UserPlus className="w-4 h-4 mr-2" /> Add Contact
            </button>
          </div>
        </div>

        {showAddContact && (
          <AddContactModal
            accountId={account.id}
            companyName={account.company_name}
            existingContacts={contacts}
            onContactAdded={handleContactAdded}
            onClose={() => setShowAddContact(false)}
          />
        )}
      </div>
    );
  }

  if (showUpload) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-base font-semibold text-app-ink">Upload Contacts</h3>
          <button onClick={() => setShowUpload(false)} className="text-sm text-app-muted hover:text-app-ink">
            Cancel
          </button>
        </div>
        <ContactCsvUpload
          accountId={account.id}
          companyName={account.company_name}
          onUploaded={handleUpload}
        />
      </div>
    );
  }

  const selectedContactScore = selectedContact ? scoreMap.get(selectedContact.id) : undefined;

  return (
    <div className="space-y-3">
      {inferredExecs.length > 0 && !execSeedDismissed && (
        <ExecutiveSeedBanner
          count={inferredExecs.length}
          executives={inferredExecs}
          onSeed={handleSeedExecutives}
          onDismiss={() => setExecSeedDismissed(true)}
          seeding={seedingExecs}
        />
      )}

      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3 min-w-0">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-semibold text-app-ink whitespace-nowrap">
                {contacts.length} contact{contacts.length !== 1 ? 's' : ''}
              </h3>
              {selectedUseCase && (
                <span className="text-[11px] text-forest-600 truncate">
                  Use case: {selectedUseCase.title}
                </span>
              )}
            </div>
          </div>

          <div className="flex items-center bg-app-canvas rounded-full p-0.5 flex-shrink-0">
            <button
              onClick={() => setViewMode('tree')}
              className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium transition ${
                viewMode === 'tree'
                  ? 'bg-app-surface text-forest-700 shadow-none'
                  : 'text-app-muted hover:text-app-ink'
              }`}
            >
              <GitFork className="w-3 h-3" />
              Tree
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium transition ${
                viewMode === 'list'
                  ? 'bg-app-surface text-forest-700 shadow-none'
                  : 'text-app-muted hover:text-app-ink'
              }`}
            >
              <List className="w-3 h-3" />
              List
            </button>
            <button
              onClick={() => setViewMode('departments')}
              className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium transition ${
                viewMode === 'departments'
                  ? 'bg-app-surface text-forest-700 shadow-none'
                  : 'text-app-muted hover:text-app-ink'
              }`}
            >
              <Building2 className="w-3 h-3" />
              Depts
            </button>
          </div>
        </div>

        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            onClick={() => setShowUpload(true)}
            className="btn-ghost !py-1.5 !px-2.5 !text-xs"
          >
            <Upload className="w-3.5 h-3.5 mr-1" /> CSV
          </button>
          <button
            onClick={() => setShowAddContact(true)}
            className="btn-ghost !py-1.5 !px-2.5 !text-xs"
          >
            <UserPlus className="w-3.5 h-3.5 mr-1" /> Add
          </button>
          <div className="relative" ref={actionsRef}>
            <button
              onClick={() => setShowActions(!showActions)}
              className="btn-ghost !py-1.5 !px-2 !text-xs"
              title="More actions"
            >
              <MoreHorizontal className="w-4 h-4" />
            </button>
            {showActions && (
              <div className="absolute right-0 top-full mt-1 w-48 bg-app-surface rounded-lg border border-app-line shadow-none z-20 py-1">
                <button
                  onClick={handleRebuildHierarchy}
                  disabled={rebuilding}
                  className="w-full flex items-center gap-2 px-3 py-2 text-xs text-app-ink hover:bg-app-canvas transition-colors disabled:opacity-50"
                >
                  {rebuilding ? <LoadingSpinner size="sm" /> : <RefreshCw className="w-3.5 h-3.5" />}
                  Rebuild Hierarchy
                </button>
                <button
                  onClick={handleExportCsv}
                  className="w-full flex items-center gap-2 px-3 py-2 text-xs text-app-ink hover:bg-app-canvas transition-colors"
                >
                  <Download className="w-3.5 h-3.5" />
                  Export CSV
                </button>
                <div className="border-t border-app-line my-1" />
                <button
                  onClick={handleDeleteAll}
                  className="w-full flex items-center gap-2 px-3 py-2 text-xs text-app-critical hover:bg-app-critical/10 transition-colors"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                  Clear All Contacts
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {missingPersonas.length > 0 && (
        <div className="flex items-start gap-2.5 p-3 bg-app-warning/10 border border-app-warning rounded-lg">
          <AlertCircle className="w-4 h-4 text-app-warning mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-xs font-semibold text-app-warning mb-1">Missing Ideal Target Personas</p>
            <p className="text-[11px] text-app-warning mb-2">
              The following researched personas are not well represented in your contact list:
            </p>
            <div className="flex flex-wrap gap-1.5">
              {missingPersonas.map((mp, i) => (
                <span key={i} className="inline-flex items-center gap-1 px-2 py-0.5 bg-app-warning/10 border border-app-warning rounded text-[10px] font-medium text-app-warning">
                  {mp.persona}
                  {mp.bestScore > 0 && (
                    <span className="text-app-warning">({mp.bestScore}% best match)</span>
                  )}
                </span>
              ))}
            </div>
          </div>
        </div>
      )}

      <ContactSearchBar
        contacts={contacts}
        ucScoreMap={scoreMap}
        onSelectResult={handleSearchSelect}
      />

      <div className={`grid gap-4 ${selectedContact ? 'grid-cols-[1fr_380px]' : 'grid-cols-1'}`}>
        <div className="min-w-0">
          {viewMode === 'tree' && (
            <OrgChartTree
              contacts={contacts}
              ucScoreMap={scoreMap}
              onSelectContact={(contact) => onContactSelect(contact?.id || null)}
              selectedContactId={selectedContact?.id}
              onAssignManager={setManagerAssignContact}
              focusContactId={focusContactId}
              onFocusHandled={() => setFocusContactId(null)}
            />
          )}
          {viewMode === 'list' && (
            <OrgChartList
              contacts={contacts}
              ucScoreMap={scoreMap}
              onSelectContact={(contact) => onContactSelect(contact?.id || null)}
              selectedContactId={selectedContact?.id}
              onAssignManager={setManagerAssignContact}
              focusContactId={focusContactId}
              onFocusHandled={() => setFocusContactId(null)}
            />
          )}
          {viewMode === 'departments' && (
            <OrgChartDepartmentView
              contacts={contacts}
              departments={departments}
              ucScoreMap={scoreMap}
              onSelectContact={(contact) => onContactSelect(contact?.id || null)}
              selectedContactId={selectedContact?.id}
              onAssignManager={setManagerAssignContact}
            />
          )}
        </div>

        {selectedContact && (
          <ContactDetailPanel
            contact={selectedContact}
            contacts={contacts}
            ucScore={selectedContactScore}
            departments={departments}
            researchedPersonas={researchedPersonas}
            onClose={() => onContactSelect(null)}
            onUpdate={handleContactUpdate}
            onGoToMessaging={() => onGoToMessaging(selectedContact.id)}
            onAssignManager={handleAssignManager}
            onPriorityOverride={onPriorityOverride}
          />
        )}
      </div>

      {managerAssignContact && (
        <ManagerAssignModal
          contact={managerAssignContact}
          allContacts={contacts}
          onAssign={handleAssignManager}
          onClose={() => setManagerAssignContact(null)}
        />
      )}

      {showAddContact && (
        <AddContactModal
          accountId={account.id}
          companyName={account.company_name}
          existingContacts={contacts}
          onContactAdded={handleContactAdded}
          onClose={() => setShowAddContact(false)}
        />
      )}
    </div>
  );
}

function ExecutiveSeedBanner({ count, executives, onSeed, onDismiss, seeding }: {
  count: number;
  executives: Contact[];
  onSeed: () => void;
  onDismiss: () => void;
  seeding: boolean;
}) {
  const [showList, setShowList] = useState(false);

  return (
    <div className="bg-app-focus/10 border border-app-focus/30 rounded-lg p-4">
      <div className="flex items-start gap-3">
        <Crown className="w-5 h-5 text-app-focus flex-shrink-0 mt-0.5" />
        <div className="flex-1">
          <p className="text-sm font-semibold text-app-focus">
            We found {count} executive{count !== 1 ? 's' : ''} from research
          </p>
          <p className="text-xs text-app-focus mt-0.5">
            Add them to your org chart as inferred contacts? They can be removed at any time.
          </p>
          {showList && (
            <div className="mt-3 space-y-1.5">
              {executives.map((exec, i) => (
                <div key={i} className="flex items-center gap-2 px-3 py-1.5 bg-app-surface rounded-lg border border-app-focus/30">
                  <Crown className="w-3 h-3 text-app-focus" />
                  <span className="text-xs font-medium text-app-ink">{exec.first_name} {exec.last_name}</span>
                  <span className="text-[10px] text-app-muted">{exec.role}</span>
                  <span className="text-[10px] px-1.5 py-0.5 bg-app-focus/10 text-app-focus rounded ml-auto">{exec.department}</span>
                </div>
              ))}
            </div>
          )}
          <div className="flex items-center gap-2 mt-3">
            <button
              onClick={onSeed}
              disabled={seeding}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-app-focus hover:bg-app-focus text-white text-xs font-medium rounded-lg transition-colors disabled:opacity-50"
            >
              {seeding ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
              {seeding ? 'Adding...' : 'Add to Org Chart'}
            </button>
            <button
              onClick={() => setShowList(!showList)}
              className="text-xs text-app-focus hover:text-app-focus font-medium transition-colors"
            >
              {showList ? 'Hide' : 'Preview'}
            </button>
            <button
              onClick={onDismiss}
              className="text-xs text-app-muted hover:text-app-muted ml-auto transition-colors"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
